import React from 'react';
import { AppBar, Toolbar, IconButton, Typography, Stack, Chip, Divider, Tooltip } from '@mui/material';
import { ArrowBack, SmartToy, Edit as EditIcon, Delete as DeleteIcon, Bookmark, BookmarkBorder, DriveFileMove as MoveIcon } from '@mui/icons-material';
import UserAvatar from '../Avatar/UserAvatar';

function ExerciseHeader({ 
  notebook, 
  example, 
  sectionExamples, 
  sectionIndex, 
  aiChatOpen, 
  onBack, 
  onToggleAI, 
  onEdit, 
  onDelete,
  onMoveToSection,
  onBookmark,
  isBookmarked,
  user,
  enableAI = true // 默认启用AI
}) {
  return (
    <AppBar 
      position="static" 
      color="default" 
      elevation={0} 
      sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
    >
      <Toolbar sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 2, justifyContent: 'space-between' }}>
        {/* Left Side - Navigation and Exercise Info */}
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center" 
          sx={{ flex: 1, minWidth: 0 }}
        >
          <IconButton 
            edge="start" 
            size="small" 
            onClick={onBack} 
            sx={{ flexShrink: 0, color: 'text.secondary' }}
          >
            <ArrowBack />
          </IconButton>
          
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {notebook?.title || '练习题'}
            </Typography>
            
            {example?.sectionTitle && (
              <Chip
                label={example.sectionTitle}
                color="primary"
                size="small"
                variant="outlined"
                sx={{ 
                  height: '24px',
                  fontSize: '0.75rem',
                  display: { xs: 'none', sm: 'flex' }
                }}
              />
            )}
            
            {sectionExamples.length > 0 && (
              <Chip
                label={`第 ${sectionIndex + 1} / ${sectionExamples.length} 题`}
                size="small"
                sx={{ 
                  height: '24px',
                  fontSize: '0.75rem',
                  display: { xs: 'none', md: 'flex' }
                }}
              />
            )}
          </Stack>
        </Stack>

        {/* Right Side: Actions */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          
          {user?.role !== 'teacher' && onBookmark && (
            <Tooltip title={isBookmarked ? '取消收藏' : '收藏到错题本'}>
              <IconButton
                size="small"
                onClick={onBookmark}
                color={isBookmarked ? 'primary' : 'default'}
              >
                {isBookmarked ? <Bookmark /> : <BookmarkBorder />}
              </IconButton>
            </Tooltip>
          )}
          
          {enableAI && (
            <Tooltip title={aiChatOpen ? '关闭AI助手' : '打开AI助手'}>
              <IconButton
                size="small"
                onClick={onToggleAI}
                color={aiChatOpen ? 'primary' : 'default'}
              >
                <SmartToy />
              </IconButton>
            </Tooltip>
          )}
          
          {user?.role === 'teacher' && (
            <>
              <Tooltip title="编辑题目">
                <IconButton size="small" onClick={onEdit} color="primary">
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {onMoveToSection && (
                <Tooltip title="移动到章节">
                  <IconButton size="small" onClick={onMoveToSection} color="default">
                    <MoveIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="删除练习题">
                <IconButton size="small" onClick={onDelete} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {user && (
            <Tooltip title={user.name || user.email || 'User'}>
              <span>
                <UserAvatar
                  user={user}
                  size={32}
                  sx={{
                    flexShrink: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s'
                    }
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export default ExerciseHeader;

